import { IUser } from "services/UserContext";

/**
 * A single row of the account dropdown, mirroring one `<a class="dropdown-item">`
 * of the HAR.com header menu.
 */
export interface IUserMenuItem {
  /**
   * Path on the HAR website. Call sites prefix it with the host they already
   * use (production for the desktop menu, `websiteHost` for the mobile drawer),
   * so this stays host-agnostic.
   */
  path: string;
  label: string;
  /** Renders the orange "NEW" pill after the label, like HAR.com does. */
  isNew?: boolean;
  /** Extra classes HAR.com puts on the row (section dividers, truncation). */
  className?: string;
  /** The non-navigating "Signed in as …" row at the top of the realtor menu. */
  isStatic?: boolean;
}

/** HAR.com profile URLs slugify the display name, e.g. "Ali Haider" -> "ali-haider". */
function slugifyName(user: IUser): string {
  return `${user?.firstname || ""} ${user?.lastname || ""}`
    .toLowerCase()
    .replace(/[,.]+/g, "") // remove commas and dots
    .trim()
    .replace(/\s+/g, "-"); // spaces to hyphens
}

/**
 * HAR.com builds the consumer's public profile URL from the slugified display
 * name plus the user id, e.g. `/answers_profile/ali-haider_.HXku7r6y12…`.
 */
function buildConsumerProfilePath(user: IUser): string {
  return `/answers_profile/${slugifyName(user)}_${user?.user_id || ""}`;
}

/**
 * The account dropdown of the HAR.com header, cloned item for item.
 *
 * Both roles share most rows in the same order; the realtor menu adds the
 * member-area block on top and the consumer menu adds Track Home Value,
 * Messages, the public profile link and Account Settings. Keep this list in
 * sync with the HAR.com header rather than editing the call sites, so the
 * desktop menu and the mobile drawer can never drift apart.
 *
 * Sign out is not included: each call site owns that row because it logs out
 * through the app instead of navigating.
 */
export function buildUserMenuItems(user: IUser): IUserMenuItem[] {
  const isConsumer = (user?.role_id ?? 0) === 1;
  const isRealtor = (user?.role_id ?? 0) === 2;

  const firstname = user?.firstname || "";
  const lastname = user?.lastname || "";

  const realtorHeader: IUserMenuItem[] = isRealtor
    ? [
        {
          path: "",
          label: `Signed in as ${firstname} ${lastname}`.trim(),
          isStatic: true,
          className: "text-truncate",
        },
        {
          path: "/mopx_platinum/account-access",
          label: "Delegated Account Access",
          className: "text-truncate",
        },
        {
          path: "/memberonlyarea",
          label: "Members Only Area",
          className: "memberonlylink",
        },
        {
          path: "/content/realtor",
          label: "HAR Member's Area",
          className: "text-truncate",
        },
        {
          path: `/${slugifyName(user)}/agent_${user?.username || ""}`,
          label: "View HAR.com Profile",
          className: "text-truncate",
        },
      ]
    : [];

  return [
    ...realtorHeader,
    {
      path: "/myaccount",
      label: "Dashboard",
      // HAR.com separates the member-area block from the rest of the menu.
      className: isRealtor ? "border-top mt-2" : undefined,
    },
    { path: "/myaccount/aichat", label: "AI Mode" },
    { path: "/myaccount/savesearch", label: "Saved Searches" },
    { path: "/myaccount/bookmarks", label: "Favorited Listings" },
    { path: "/myaccount/comingsoon", label: "Coming Soon Alerts" },
    {
      path: isConsumer ? "/myaccount/myagent" : "/connectwithagent",
      label: "My Agent",
    },
    ...(isConsumer
      ? [
          {
            path: "/myaccount/trackhomevalue",
            label: "Track Home Value",
            isNew: true,
          },
          { path: "/myaccount/messages", label: "Messages", isNew: true },
        ]
      : []),
    { path: "/myaccount/DownPayment", label: "Down Payment Assistance" },
    {
      path: isConsumer
        ? "/myaccount/recommendations"
        : "/moa_myaccount_personalinfo/recommendations?tab=Requested&inner_tab=All",
      label: "Recommendations",
    },
    { path: "/myaccount/community", label: "Ask a Pro" },
    { path: "/myaccount/reviews", label: "Write Reviews" },
    { path: "/myaccount/notinterest", label: "Not Interested Listings" },
    ...(isConsumer
      ? [
          {
            path: buildConsumerProfilePath(user),
            label: "Your Profile on HAR.com",
          },
        ]
      : []),
    { path: "/myaccount/take-mobile", label: "Download HAR.com App" },
    { path: "/myaccount/traffic-report", label: "Traffic Reports" },
    ...(isConsumer
      ? [{ path: "/myaccount/settings", label: "Account Settings" }]
      : []),
  ];
}
